import { useRef } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";

const CASES = [
  { company: "Global CPG Leader", sub: "Reinventing the month-end close", quote: "Agents reconcile millions of transactions across 12 ERPs and surface only the exceptions that need a human.", m: [["45%", "Faster Close"], ["12", "ERPs Unified"]], tags: ["Reconciliation", "Finance"] },
  { company: "Fortune 100 Bank", sub: "Trusted context for finance ops", quote: "A shared semantic layer means every team and agent works from the same governed numbers.", m: [["3\u00D7", "Faster Recon"], ["100%", "Governed"]], tags: ["Semantic Layer", "BFSI"] },
  { company: "Industrial Manufacturer", sub: "Smarter demand & supply planning", quote: "Agentic planning balances service levels and working capital across a global network in real time.", m: [["20%", "Lower Inventory"], ["Live", "In Production"]], tags: ["Planning", "Supply Chain"] },
  { company: "Global Retail Group", sub: "Campaign orchestration at scale", quote: "Planning, content and activation are coordinated end-to-end \u2014 weeks of work now happen in hours.", m: [["2\u00D7", "Throughput"], ["Live", "Channels"]], tags: ["Marketing", "Content"] },
];

/* F · Horizontal split — dark metric panel + content */
const CardF = ({ c }) => (
  <div className="shrink-0 w-[380px] sm:w-[440px] snap-start rounded-2xl overflow-hidden border border-[#3E2818]/10 bg-white grid grid-cols-[0.42fr_1fr]">
    <div className="bg-gradient-to-b from-[#2E1B0C] to-[#160D05] p-5 flex flex-col justify-center gap-5">
      {c.m.map(([n, l]) => <div key={l}><div className="font-display text-3xl font-medium text-[#E8AA68] leading-none">{n}</div><div className="text-[10px] uppercase tracking-[0.12em] text-white/55 mt-1">{l}</div></div>)}
    </div>
    <div className="p-5 flex flex-col">
      <p className="font-display text-lg font-medium text-[#3E2818] leading-tight">{c.company}</p>
      <p className="text-[11.5px] text-[#3E2818]/55">{c.sub}</p>
      <p className="text-[13px] text-[#3E2818]/70 leading-relaxed mt-3 flex-1">{c.quote}</p>
      <div className="mt-3 flex flex-wrap gap-2">{c.tags.map((t) => <span key={t} className="text-[11px] font-semibold rounded-md px-2.5 py-1 bg-[#8A5730]/10 text-[#8A5730]">{t}</span>)}</div>
    </div>
  </div>
);

/* G · Editorial index — big number, minimal, hairline */
const CardG = ({ c, i }) => (
  <div className="shrink-0 w-[300px] sm:w-[330px] snap-start rounded-2xl bg-transparent border border-[#3E2818]/15 p-6 flex flex-col hover:bg-white/50 transition-colors duration-300">
    <div className="flex items-baseline justify-between">
      <span className="font-display text-5xl font-medium text-[#3E2818]/15 leading-none">{String((i % 4) + 1).padStart(2, "0")}</span>
      <span className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#8A5730]">{c.tags[1]}</span>
    </div>
    <p className="font-display text-xl font-medium text-[#3E2818] mt-5">{c.company}</p>
    <p className="text-[12px] text-[#3E2818]/55 mt-0.5">{c.sub}</p>
    <p className="text-[13px] text-[#3E2818]/70 leading-relaxed mt-4 flex-1">{c.quote}</p>
    <div className="h-px my-4 bg-[#3E2818]/12" />
    <div className="flex gap-8">{c.m.map(([n, l]) => <div key={l}><div className="font-display text-xl font-medium text-[#8A5730]">{n}</div><div className="text-[10px] uppercase tracking-[0.12em] text-[#3E2818]/50">{l}</div></div>)}</div>
  </div>
);

/* H · Cover band card */
const CardH = ({ c, i }) => (
  <div className="shrink-0 w-[300px] sm:w-[340px] snap-start rounded-2xl bg-white border border-[#3E2818]/10 overflow-hidden flex flex-col">
    <div className={`relative h-28 p-5 flex items-end ${["bg-gradient-to-br from-[#8A5730] to-[#3E2818]", "bg-gradient-to-br from-[#EDA13A] to-[#8A5730]", "bg-gradient-to-br from-[#2E1B0C] to-[#5A3418]", "bg-gradient-to-br from-[#5A3418] to-[#211307]"][i % 4]}`}>
      <p className="font-display text-xl font-medium text-white">{c.company}</p>
      <span className="absolute top-4 right-4 font-mono2 text-[10px] uppercase tracking-[0.16em] text-white/80">{c.tags[1]}</span>
    </div>
    <div className="p-5 flex-1 flex flex-col">
      <p className="text-[11.5px] text-[#3E2818]/55">{c.sub}</p>
      <p className="text-[13px] text-[#3E2818]/70 leading-relaxed mt-2 flex-1">{c.quote}</p>
      <div className="mt-4 flex gap-8">{c.m.map(([n, l]) => <div key={l}><div className="font-display text-2xl font-medium text-[#8A5730]">{n}</div><div className="text-[10px] uppercase tracking-[0.12em] text-[#3E2818]/50 mt-0.5">{l}</div></div>)}</div>
    </div>
  </div>
);

const Carousel = ({ Card }) => {
  const ref = useRef(null);
  const scroll = (d) => ref.current?.scrollBy({ left: d * 380, behavior: "smooth" });
  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="font-mono2 text-[11px] uppercase tracking-[0.28em] text-[#8A5730] mb-4">Powering Enterprises of All Sizes</p>
          <h2 className="font-display text-3xl lg:text-[2.75rem] font-medium tracking-tight text-[#3E2818] leading-[1.05]">Agents <span className="italic">in production</span><br />not just demos.</h2>
        </div>
        <div className="hidden sm:flex gap-2">
          <button onClick={() => scroll(-1)} className="w-11 h-11 rounded-full border border-[#3E2818]/25 flex items-center justify-center text-[#3E2818] hover:bg-[#211307] hover:text-[#F7F5F0] hover:border-[#211307] transition-colors"><ArrowLeft size={17} /></button>
          <button onClick={() => scroll(1)} className="w-11 h-11 rounded-full border border-[#3E2818]/25 flex items-center justify-center text-[#3E2818] hover:bg-[#211307] hover:text-[#F7F5F0] hover:border-[#211307] transition-colors"><ArrowRight size={17} /></button>
        </div>
      </div>
      <div ref={ref} className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-1 px-1 items-stretch [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {[...CASES, ...CASES].map((c, i) => <Card key={i} c={c} i={i} />)}
      </div>
    </div>
  );
};

const OPTIONS = [
  { id: "f", title: "Option F · Horizontal Split", note: "Wide cards with a dark metric panel on the left and the story on the right. Metrics read instantly; feels substantial and premium.", Card: CardF },
  { id: "g", title: "Option G · Editorial Index", note: "Minimal outlined cards with a large faint index number. Very editorial and understated \u2014 lets the words carry it.", Card: CardG },
  { id: "h", title: "Option H · Cover Band", note: "A colored cover band carries the company name (can become a logo/photo later), with the quote and metrics below. Brandable and lively.", Card: CardH },
];

export default function CaseStudyOptions5() {
  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A5730]">Success Stories · Even More Templates</p>
        <h1 className="font-display text-2xl lg:text-3xl font-medium mt-2 tracking-tight text-[#3E2818]">A third set of card styles</h1>
        <p className="text-sm text-[#3E2818]/60 mt-3 max-w-2xl leading-relaxed">Placeholder stories. Use the arrows or scroll. Tell me the letter.</p>

        <div className="mt-12 space-y-20">
          {OPTIONS.map((o) => (
            <div key={o.id}>
              <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#B87A32] mb-6">{o.title}</p>
              <Carousel Card={o.Card} />
              <p className="text-[12px] text-[#3E2818]/55 mt-4 leading-snug max-w-3xl">{o.note}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
